/**
 * Positioning Utilities
 * 
 * Shared helper functions for node positioning calculations.
 */

import { HORIZONTAL_GAP, VERTICAL_GAP, DEFAULT_NODE_HEIGHT, PADDING, getNodeDimensions } from './constants';

/**
 * Check if two line segments intersect
 */
export function segmentsIntersect(p1, q1, p2, q2) {
  const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const d1 = cross(p2, q2, p1);
  const d2 = cross(p2, q2, q1);
  const d3 = cross(p1, q1, p2);
  const d4 = cross(p1, q1, q2);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
      ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }
  return false;
}

/**
 * Check if two rectangles overlap (with gap consideration)
 */
export function rectsOverlap(r1, r2) {
  return !(r1.right + HORIZONTAL_GAP < r2.left || 
           r2.right + HORIZONTAL_GAP < r1.left || 
           r1.bottom + HORIZONTAL_GAP < r2.top || 
           r2.bottom + HORIZONTAL_GAP < r1.top);
}

/**
 * Create a Y position calculator based on layer positions
 */
export function createYCalculator(layerYPositions) {
  // Calculate average layer height
  let avgLayerHeight = VERTICAL_GAP + DEFAULT_NODE_HEIGHT;
  if (layerYPositions.length >= 2) {
    avgLayerHeight = (layerYPositions[layerYPositions.length - 1] - layerYPositions[0]) / (layerYPositions.length - 1);
  }

  return function calculateY(yLayer) {
    if (layerYPositions.length === 0) return PADDING;
    if (layerYPositions.length === 1) return layerYPositions[0] + yLayer * avgLayerHeight;
    
    const floorLayer = Math.floor(yLayer);
    const ceilLayer = Math.ceil(yLayer);
    const fraction = yLayer - floorLayer;
    
    if (yLayer < 0) {
      return layerYPositions[0] + yLayer * avgLayerHeight;
    } else if (floorLayer >= layerYPositions.length - 1) {
      const lastY = layerYPositions[layerYPositions.length - 1];
      const extraLayers = yLayer - (layerYPositions.length - 1);
      return lastY + extraLayers * avgLayerHeight;
    } else if (floorLayer === ceilLayer) {
      return layerYPositions[floorLayer];
    } else {
      const floorY = layerYPositions[floorLayer];
      const ceilY = layerYPositions[ceilLayer];
      return floorY + (ceilY - floorY) * fraction;
    }
  };
}

/**
 * Resolve overlaps within a single layer
 */
export function resolveLayerOverlaps(layer, positions, nodeDimensions) {
  if (layer.length < 2) return;
  
  const sortedNodes = [...layer].sort((a, b) => {
    return (positions[a]?.x || 0) - (positions[b]?.x || 0);
  });
  
  let hasOverlap = true;
  let iterations = 0;
  const maxIterations = layer.length * 2;
  
  while (hasOverlap && iterations < maxIterations) {
    hasOverlap = false;
    iterations++;
    
    sortedNodes.sort((a, b) => {
      return (positions[a]?.x || 0) - (positions[b]?.x || 0);
    });
    
    for (let i = 1; i < sortedNodes.length; i++) {
      const prevId = sortedNodes[i - 1];
      const currId = sortedNodes[i];
      
      const prevX = positions[prevId]?.x || 0;
      const currX = positions[currId]?.x || 0;
      
      const prevWidth = getNodeDimensions(prevId, nodeDimensions).width;
      const minDistance = prevWidth + HORIZONTAL_GAP;
      const actualDistance = currX - prevX;
      
      if (actualDistance < minDistance) {
        const adjustment = minDistance - actualDistance;
        positions[currId] = {
          ...positions[currId],
          x: currX + adjustment,
        };
        hasOverlap = true;
      }
    }
  }
}

/**
 * Get reference path bounds from positioned nodes
 */
export function getRefPathBounds(positions, graph, level, nodeDimensions) {
  let refPathMinX = Infinity;
  let refPathMaxX = -Infinity;
  
  for (const [nodeId, pos] of Object.entries(positions)) {
    const nodeLevel = graph.nodeLevels?.get(nodeId) || 0;
    if (nodeLevel < level) {
      const nodeWidth = getNodeDimensions(nodeId, nodeDimensions).width;
      refPathMinX = Math.min(refPathMinX, pos.x);
      refPathMaxX = Math.max(refPathMaxX, pos.x + nodeWidth);
    }
  }
  
  return { refPathMinX, refPathMaxX };
}

/**
 * Collect reference path edge vectors for intersection testing
 */
export function collectRefPathVectors(positions, graph, level, feedbackLayerInfo, nodeDimensions) {
  const refPathVectors = [];
  
  // Forward edges between nodes at level < current
  for (const edge of graph.forwardEdges) {
    const fromLevel = graph.nodeLevels?.get(edge.from) || 0;
    const toLevel = graph.nodeLevels?.get(edge.to) || 0;
    if (fromLevel < level && toLevel < level) {
      const fromPos = positions[edge.from];
      const toPos = positions[edge.to];
      if (!fromPos || !toPos) continue;
      
      const fromWidth = getNodeDimensions(edge.from, nodeDimensions).width;
      const fromHeight = getNodeDimensions(edge.from, nodeDimensions).height;
      const toWidth = getNodeDimensions(edge.to, nodeDimensions).width;
      
      refPathVectors.push({
        from: { x: fromPos.x + fromWidth / 2, y: fromPos.y + fromHeight },
        to: { x: toPos.x + toWidth / 2, y: toPos.y },
        fromId: edge.from,
        toId: edge.to,
      });
    }
  }
  
  // Edges from previous level feedback loops
  if (level > 1) {
    for (const [nodeId, info] of feedbackLayerInfo) {
      if ((info.nestingLevel || 1) < level) {
        const fromPos = positions[nodeId];
        if (!fromPos) continue;
        
        const children = graph.allChildren.get(nodeId) || [];
        for (const childId of children) {
          const childPos = positions[childId];
          if (!childPos) continue;
          
          const fromWidth = getNodeDimensions(nodeId, nodeDimensions).width;
          const fromHeight = getNodeDimensions(nodeId, nodeDimensions).height;
          const toWidth = getNodeDimensions(childId, nodeDimensions).width;
          
          refPathVectors.push({
            from: { x: fromPos.x + fromWidth / 2, y: fromPos.y + fromHeight },
            to: { x: childPos.x + toWidth / 2, y: childPos.y },
            fromId: nodeId,
            toId: childId,
          });
        }
      }
    }
  }
  
  return refPathVectors;
}

